/**
 * DiagnosticViewer — view diagnostic reports, trigger diagnostic mode.
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Activity, ChevronDown, ChevronUp } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { useGatewayDiagnostics, type DiagnosticSession } from '@/hooks/useGatewayDiagnostics';
import { useGatewayCommands } from '@/hooks/useGatewayCommands';
import type { GatewayDevice, GatewayDiagnosticReport } from '@/types/gateway';
import { toast } from 'sonner';

interface DiagnosticViewerProps {
  device: GatewayDevice;
}

export function DiagnosticViewer({ device }: DiagnosticViewerProps) {
  const { sessions, isLoading } = useGatewayDiagnostics(device.id);
  const { issueCommand, isIssuing } = useGatewayCommands(device.id);
  const [expandedSession, setExpandedSession] = useState<string | null>(null);

  const handleStartDiagnostic = async () => {
    try {
      await issueCommand({ commandType: 'diagnostic_request' });
      toast.success('Diagnostic mode started (5 minutes)');
    } catch {
      toast.error('Failed to start diagnostic mode');
    }
  };

  return (
    <div className="space-y-4">
      {/* Trigger diagnostic mode */}
      <Card className="bg-wt-bg-secondary border-border">
        <CardContent className="py-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-wt-text-primary">Diagnostic Mode</p>
            <p className="text-xs text-wt-text-secondary">
              {device.diagnostic_mode
                ? 'Active — enhanced telemetry being captured'
                : 'Capture detailed bus and WiFi data for 5 minutes'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {device.diagnostic_mode && (
              <Badge variant="outline" className="bg-wt-amber-primary/10 text-wt-amber-primary border-wt-amber-primary/20">
                Active
              </Badge>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={handleStartDiagnostic}
              disabled={isIssuing || device.diagnostic_mode}
            >
              {isIssuing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start Diagnostic Mode'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sessions list */}
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Diagnostic Reports
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-wt-text-secondary" />
            </div>
          ) : sessions.length === 0 ? (
            <p className="py-6 text-center text-sm text-wt-text-secondary">
              No diagnostic reports. Start diagnostic mode to capture detailed bus and WiFi data.
            </p>
          ) : (
            <div className="space-y-2">
              {sessions.map((session) => (
                <SessionCard
                  key={session.session_id}
                  session={session}
                  expanded={expandedSession === session.session_id}
                  onToggle={() =>
                    setExpandedSession(
                      expandedSession === session.session_id ? null : session.session_id
                    )
                  }
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SessionCard
// ---------------------------------------------------------------------------

function SessionCard({
  session,
  expanded,
  onToggle,
}: {
  session: DiagnosticSession;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border border-border/50 rounded-lg">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 text-left hover:bg-wt-bg-tertiary/50 rounded-lg"
      >
        <div>
          <p className="text-sm text-wt-text-primary">
            Session {session.session_id.slice(0, 8)}
          </p>
          <p className="text-xs text-wt-text-secondary">
            {formatDistanceToNow(new Date(session.start), { addSuffix: true })} · {session.reports.length} report{session.reports.length !== 1 ? 's' : ''}
          </p>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-wt-text-secondary" />
        ) : (
          <ChevronDown className="w-4 h-4 text-wt-text-secondary" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-border/50 p-3 space-y-3">
          {session.reports.map((report) => (
            <ReportDetail key={report.id} report={report} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReportDetail
// ---------------------------------------------------------------------------

function getResponseTimeColor(ms: number): string {
  if (ms < 100) return 'text-wt-green-primary';
  if (ms < 300) return 'text-wt-amber-primary';
  return 'text-wt-red-primary';
}

function ReportDetail({ report }: { report: GatewayDiagnosticReport }) {
  const payload = report.payload ?? {};
  const busSamples = (payload.bus_samples ?? []) as Array<{
    address?: number;
    request_hex?: string;
    response_hex?: string;
    response_time_ms?: number;
    status?: string;
  }>;
  const wifiEvents = (payload.wifi_events ?? []) as Array<{
    event?: string;
    timestamp?: string;
  }>;
  const registerTiming = (payload.register_timing ?? []) as Array<{
    group?: string;
    read_time_ms?: number;
  }>;

  return (
    <div className="bg-wt-bg-primary rounded p-3 space-y-3">
      <p className="text-xs text-wt-text-secondary">
        {format(new Date(report.reported_at), 'yyyy-MM-dd HH:mm:ss')}
      </p>

      {/* Bus Samples */}
      {busSamples.length > 0 && (
        <div>
          <p className="text-xs font-medium text-wt-text-secondary mb-1">Bus Samples</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-wt-text-secondary">
                  <th className="text-left pr-2">Addr</th>
                  <th className="text-left pr-2">Request</th>
                  <th className="text-left pr-2">Response</th>
                  <th className="text-right pr-2">Time</th>
                  <th className="text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {busSamples.map((s, i) => (
                  <tr key={i}>
                    <td className="font-mono-numeric pr-2">{s.address ?? '--'}</td>
                    <td className="font-mono-numeric pr-2 text-wt-text-secondary truncate max-w-[120px]">
                      {s.request_hex ?? '--'}
                    </td>
                    <td className="font-mono-numeric pr-2 text-wt-text-secondary truncate max-w-[120px]">
                      {s.response_hex ?? '--'}
                    </td>
                    <td className={`font-mono-numeric text-right pr-2 ${s.response_time_ms != null ? getResponseTimeColor(s.response_time_ms) : ''}`}>
                      {s.response_time_ms != null ? `${s.response_time_ms}ms` : '--'}
                    </td>
                    <td>{s.status ?? '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* WiFi Events */}
      {wifiEvents.length > 0 && (
        <div>
          <p className="text-xs font-medium text-wt-text-secondary mb-1">WiFi Events</p>
          <div className="space-y-0.5">
            {wifiEvents.map((e, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="text-wt-text-secondary font-mono-numeric">
                  {e.timestamp ? format(new Date(e.timestamp), 'HH:mm:ss') : '--'}
                </span>
                <span className="text-wt-text-primary">{e.event}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Register Timing */}
      {registerTiming.length > 0 && (
        <div>
          <p className="text-xs font-medium text-wt-text-secondary mb-1">Register Timing</p>
          <div className="space-y-0.5">
            {registerTiming.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-wt-text-secondary">{r.group}</span>
                <span className={`font-mono-numeric ${r.read_time_ms != null ? getResponseTimeColor(r.read_time_ms) : ''}`}>
                  {r.read_time_ms != null ? `${r.read_time_ms}ms` : '--'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Raw payload fallback if no structured sections */}
      {busSamples.length === 0 && wifiEvents.length === 0 && registerTiming.length === 0 && (
        <pre className="text-xs font-mono-numeric text-wt-text-secondary overflow-auto max-h-32">
          {JSON.stringify(payload, null, 2)}
        </pre>
      )}
    </div>
  );
}
